import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { AbstractControl } from "@angular/forms";
import { environment } from "@environments/environment";
import { Observable } from "rxjs";
import { Entitlement, Plan, Product, ProductDetails, Sku } from "../models";

interface ApiKeyResponse {
    entitlement_id: string;
    message: string;
    secret: string;
}

@Injectable({
    providedIn: "root",
})
export class ApiService {
    private apiUrl = environment.brain_base_url;

    constructor(private http: HttpClient, private afAuth: AngularFireAuth) { }

    private signUpAPI = "/sign_up";
    private getServicesAPI = "/services/subscriptions";
    private purchasesAPI = "/store/purchases";
    private getCatalogAPIEnding = "/products";
    private getCatalogAPI = "/catalog/";
    private entitlementAPI = "/entitlements";

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    userSignUp(name: any, email: any): Observable<any> {
        const request = { data: { email: email, name: name, send_invite: false } };
        return this.http.post(this.apiUrl + this.signUpAPI, request);
    }

    getUsersByService(subscriptionId: string): Observable<Entitlement[]> {
        return this.http.get<Array<Entitlement>>(
            this.apiUrl +
            this.getServicesAPI +
            "/" +
            subscriptionId +
            this.entitlementAPI
        );
    }

    createUserForService(
        subscriptionId: string,
        name: string,
        type: string,
        active: boolean,
        email: string,
        roles: string[]
    ): Observable<any> {
        let reqbody;
        type === "apikey"
            ? (reqbody = {
                name: name,
                type: type,
                active: active,
            })
            : (reqbody = {
                name: name,
                type: type,
                active: active,
                email: email,
                roles: roles,
            });
        return this.http.post(
            this.apiUrl +
            this.getServicesAPI +
            "/" +
            subscriptionId +
            this.entitlementAPI,
            reqbody
        );
    }

    updateUserForService(
        subscriptionId: string,
        entitlement_id: string,
        name?: string,
        roles?: string | any[]
    ): Observable<any> {
        let reqbody;
        name && roles && roles.length > 0
            ? (reqbody = { name: name, roles: roles })
            : roles && roles.length > 0
                ? (reqbody = { roles: roles })
                : (reqbody = { name: name });

        return this.http.put(
            this.apiUrl +
            this.getServicesAPI +
            "/" +
            subscriptionId +
            this.entitlementAPI +
            "/" +
            entitlement_id,
            reqbody
        );
    }

    removeUserFromService(subscription_id: string, entitlement_id: string): Observable<any> {
        return this.http.delete(
            this.apiUrl +
            this.getServicesAPI +
            "/" +
            subscription_id +
            this.entitlementAPI +
            "/" +
            entitlement_id
        );
    }

    validateAPI(control: AbstractControl): { validAPI: boolean; } {
        if (!control.value.match(/^[-a-zA-Z0-9_ ]+$/g)) {
            return { validAPI: true };
        }
        return null;
    }

    public addApiKey(
        subscription_id: string,
        name: string
    ): Observable<ApiKeyResponse> {
        return this.createUserForService(
            subscription_id,
            name,
            `apikey`,
            true,
            ``,
            []
        ) as Observable<ApiKeyResponse>;
    }

    public updateApiKey(
        subscription_id: string,
        name: string
    ): Observable<ApiKeyResponse> {
        return this.createUserForService(
            subscription_id,
            name,
            `apikey`,
            true,
            ``,
            []
        ) as Observable<ApiKeyResponse>;
    }

    getProducts(catalogName: string): Observable<Product[]> {
        return this.http.get<Array<Product>>(
            this.apiUrl + this.getCatalogAPI + catalogName + this.getCatalogAPIEnding
        );
    }

    getProduct(id: string, catalogName: string): Observable<ProductDetails> {
        return this.http.get<ProductDetails>(
            this.apiUrl +
            this.getCatalogAPI +
            catalogName +
            this.getCatalogAPIEnding +
            "/" +
            id
        );
    }

    getProductContacts(id: string, catalogName: string): Observable<string[]> {
        return this.http.get<Array<string>>(
            this.apiUrl +
            this.getCatalogAPI +
            catalogName +
            this.getCatalogAPIEnding +
            "/" +
            id +
            "/" +
            "contact_info"
        );
    }

    getSkus(productId: string, catalogName: string): Observable<Sku[]> {
        return this.http.get<Array<Sku>>(
            this.apiUrl +
            this.getCatalogAPI +
            catalogName +
            this.getCatalogAPIEnding +
            "/" +
            productId +
            "/skus"
        );
    }
    getSku(productId: string, skuId: string, catalogName: string): Observable<Sku> {
        return this.http.get<Sku>(
            this.apiUrl +
            this.getCatalogAPI +
            catalogName +
            this.getCatalogAPIEnding +
            "/" +
            productId +
            "/skus/" +
            skuId
        );
    }

    getSkuPlans(productId: string, skuId: string, catalogName: string): Observable<Plan[]> {
        return this.http.get<Array<Plan>>(
            this.apiUrl +
            this.getCatalogAPI +
            catalogName +
            this.getCatalogAPIEnding +
            "/" +
            productId +
            "/skus/" +
            skuId +
            "/plans"
        );
    }

    getSkuPlan(
        productId: string,
        skuId: string,
        paymentId: string,
        catalogName: string
    ): Observable<Plan> {
        return this.http.get<Plan>(
            this.apiUrl +
            this.getCatalogAPI +
            catalogName +
            this.getCatalogAPIEnding +
            "/" +
            productId +
            "/skus/" +
            skuId +
            "/plans/" +
            paymentId
        );
    }

    getServicesSubscribed(): Observable<any> {
        return this.http.get(this.apiUrl + this.getServicesAPI, {
            params: {
                kind: "service",
            },
        });
    }

    getServiceSubscribed(subId: string): Observable<any> {
        return this.http.get(this.apiUrl + this.getServicesAPI + "/" + subId);
    }

    setNickname(title: string, subId: string): Observable<any> {
        const data = {
            service_nickname: title,
        };
        return this.http.put(this.apiUrl + this.getServicesAPI + "/" + subId, data);
    }

    getPurchases(): Observable<any> {
        return this.http.get(this.apiUrl + this.purchasesAPI);
    }

    getPurchase(subId: string): Observable<any> {
        return this.http.get(this.apiUrl + this.purchasesAPI + "/" + subId);
    }

    postPurchase(
        engCode: string,
        prodId: string,
        skuId: string,
        planId: string,
        tosId: string
    ): Observable<any> {
        const req = {
            engagement_code: engCode,
            product_id: prodId,
            sku_id: skuId,
            plan_id: planId,
            tos_id: tosId,
        };
        return this.http.post(this.apiUrl + this.purchasesAPI, req);
    }
}
